<template>
    <div class="block-editor ticket-update">
        <section>
            <div class="wrapper">
                <div class="editor-row-title">Ticket ID</div>
                <TiptapEditor
                    placeholder="Ticket ID"
                    :show-attributes="true"
                    v-model="clone.ticketId"
                    automation-mode
                />
            </div>
        </section>
        <section>
            <div class="title">Updateable Fields</div>
            <div class="fields-wrapper">
                <div v-for="(field, index) in clone.fields" :key="index" class="field-row">
                    <div class="field-container">
                        <label>Field</label>
                        <CustomPicker
                            v-model="field.field"
                            :items="availableFields"
                            idKey="name"
                            labelKey="label"
                            title="Select Field"
                            @input="onFieldSelect(field)"
                        />
                    </div>
                    <div class="value-container">
                        <div class="value-header">
                            <label>Value</label>
                            <div class="mode-switch">
                                <span class="switch-label">Dynamic</span>
                                <a-switch
                                    size="small"
                                    :checked="field.mode === 'dynamic'"
                                    @change="(checked) => onModeChange(field,index)"
                                />
                            </div>
                        </div>
                        <template v-if="field.mode === 'static'">
                            <CustomPicker
                                v-if="field.field === 'status'"
                                v-model="field.value"
                                :items="statusOptions"
                                idKey="title"
                                labelKey="title"
                                :placeholder="getPlaceholderForField(field.field)"
                            />
                            <CustomPicker
                                v-else-if="field.field === 'rate'"
                                v-model="field.value"
                                :items="rateOptions"
                                idKey="value"
                                labelKey="label"
                                :placeholder="getPlaceholderForField(field.field)"
                            />
                            <CustomPicker
                                v-else-if="field.field === 'assignedUser'"
                                v-model="field.value"
                                :items="userOptions"
                                idKey="id"
                                labelKey="name"
                                :placeholder="getPlaceholderForField(field.field)"
                            />
                            <TiptapEditor
                                v-else-if="field.field === 'tag'"
                                v-model="field.value"
                                :placeholder="getPlaceholderForField(field.field)"
                            />
                        </template>
                        <template v-else>
                            <TiptapEditor
                                v-model="field.value"
                                :placeholder="getPlaceholderForField(field.field)"
                                automation-mode
                                :show-attributes="true"
                            />
                        </template>
                    </div>

                    <SupsisButton @click="removeField(index)" icon danger xsmall>
                        <i class="material-icons">delete</i>
                    </SupsisButton>
                </div>
                <a-button type="primary" @click="addField">
                    <a-icon type="plus" />
                    Add Field
                </a-button>
            </div>
        </section>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import { mapState } from "vuex";
import CustomPicker from "../../../../../../components/pickers/CustomPicker.vue";
import { readTicketStatus } from "../../../../../../api/ticket";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

export default {
    name: "TicketUpdate",
    components: {
        TiptapEditor,
        CustomPicker,
        SupsisButton,
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || { fields: [] }),
            availableFields: [
                { name: "status", label: "Status" },
                { name: "assignedUser", label: "Assigned User" },
                { name: "rate", label: "Rate" },
                { name: "tag", label: "Tag" },
            ],
            rateOptions: [
                { value: 0, label: "0 - Çok Kötü" },
                { value: 1, label: "1 - Kötü" },
                { value: 2, label: "2 - Orta" },
                { value: 3, label: "3 - İyi" },
                { value: 4, label: "4 - Çok İyi" },
                { value: 5, label: "5 - Mükemmel" },
            ],
            statusList: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
        userOptions() {
            return this.users.map((user) => ({
                id: user._id,
                name: `${user.name} (${user.email})`,
            }));
        },
        statusOptions() {
            return this.statusList.map((status) => ({
                title: status.title,
                value: status.title,
            }));
        },
    },
    methods: {
        fetchTicketStatus() {
            readTicketStatus().then(({ data }) => {
                this.statusList = data;
            });
        },
        addField() {
            this.clone.fields.push({ 
                field: null, 
                value: null, 
                mode: 'static' 
            });
        },
        removeField(index) {
            this.clone.fields.splice(index, 1);
        },
        getPlaceholderForField(field) {
            switch (field) {
                case "status":
                    return "Select Status";
                case "assignedUser":
                    return "Select User";
                case "rate":
                    return "Select Rate";
                case "tag":
                    return "Enter Tag";
                default:
                    return "";
            }
        },
        onFieldSelect(field) {
            // Eğer field.value null ise, ilgili component'in default değerini atayalım
            if (field.value === null) {
                switch (field.field) {
                    case "status":
                        field.value = this.statusOptions[0]?.title;
                        break;
                    case "assignedUser":
                        field.value = null; // AgentPicker'ın varsayılan değeri null olmalı
                        break;
                    case "rate":
                        field.value = this.rateOptions[0]?.value;
                        break;
                    case "tag":
                        field.value = ""; // TiptapEditor'ın varsayılan değeri boş string olmalı
                        break;
                }
            }
        },
        onModeChange(field, index) {
            this.clone.fields[index].mode = field.mode === "dynamic" ? "static" : "dynamic";
            this.clone = _.cloneDeep(this.clone);
        }
    },
    mounted() {
        this.fetchTicketStatus();
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.block-editor.ticket-update {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 30px;
    section {
        margin-bottom: 10px;
    }

    .title {
        margin-bottom: 10px;
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
    }

    .fields-wrapper {
        .field-row {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;
            align-items: center;
            border-bottom: 1px solid #e0e0e0;

            > * {
                flex: 1;
            }

            .field-container,
            .value-container {
                display: flex;
                flex-direction: column;
            }

            .field-label {
                font-weight: 600;
            }

            .value-container {
                .value-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;

                    .mode-switch {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .switch-label {
                            font-size: 12px;
                            color: rgba(0, 0, 0, 0.65);
                        }
                    }
                }
            }
        }
    }
}
</style>
